/**
 * Breadcrumb Utility
 *
 * Provides utilities for generating breadcrumb trails from module specs.
 */

import type { TFunction } from 'i18next';
import { moduleRegistry, getBreadcrumbs as registryGetBreadcrumbs } from '../registry';
import type { BreadcrumbTrail, Breadcrumb, ModuleSpec } from '../types';
import { Home } from 'lucide-react';

/**
 * Generate a breadcrumb trail for the given pathname.
 */
export function generateBreadcrumbs(pathname: string, t: TFunction): BreadcrumbTrail {
  return registryGetBreadcrumbs(pathname, t);
}

/**
 * Generate breadcrumbs with Home icon on first segment.
 */
export function generateBreadcrumbsWithIcons(pathname: string, t: TFunction): BreadcrumbTrail {
  const breadcrumbs = generateBreadcrumbs(pathname, t);

  // Add Home icon to first breadcrumb
  if (breadcrumbs.length > 0) {
    breadcrumbs[0] = {
      ...breadcrumbs[0],
      icon: Home,
    };
  }

  return breadcrumbs;
}

/**
 * Resolve parent module chain for a given module.
 */
export function resolveParentChain(moduleId: string): ModuleSpec[] {
  const chain: ModuleSpec[] = [];
  let current = moduleRegistry.getById(moduleId);

  while (current) {
    chain.unshift(current);
    if (current.nav?.parent) {
      current = moduleRegistry.getById(current.nav.parent);
    } else {
      break;
    }
  }

  return chain;
}

/**
 * Get the parent module for a given module ID.
 */
export function getParentModule(moduleId: string): ModuleSpec | undefined {
  const module = moduleRegistry.getById(moduleId);
  if (!module?.nav?.parent) return undefined;
  return moduleRegistry.getById(module.nav.parent);
}

/**
 * Check if a module has a parent.
 */
export function hasParent(moduleId: string): boolean {
  const module = moduleRegistry.getById(moduleId);
  return !!module?.nav?.parent;
}

/**
 * Get breadcrumb label for a module.
 */
export function getModuleBreadcrumbLabel(moduleId: string, t: TFunction): string {
  return t(`modules:${moduleId}.title`, { defaultValue: moduleId });
}

/**
 * Build a single breadcrumb from a module spec.
 */
export function buildBreadcrumbFromModule(
  module: ModuleSpec,
  t: TFunction,
  isLast: boolean
): Breadcrumb {
  return {
    label: getModuleBreadcrumbLabel(module.id, t),
    path: isLast ? undefined : module.nav?.path,
    moduleId: module.id,
  };
}
